import React from "react";
import { Link } from "react-router-dom";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Mail, Phone, Building } from "lucide-react";
import { Skeleton } from "@/components/ui/skeleton";
import { Card, CardContent } from "@/components/ui/card";
import { getAvatarColor, formatPhoneNumber } from "@/lib/utils";

const statusColors = {
  new: "bg-slate-100 text-slate-700",
  contacted: "bg-purple-100 text-purple-700",
  qualifying: "bg-amber-100 text-amber-700",
  converted: "bg-emerald-100 text-emerald-700",
  lost: "bg-slate-100 text-slate-700"
};

export default function LeadListView({ 
  leads, 
  isLoading, 
  onViewLead,
  selectedLeadIds = [],
  onToggleSelect
}) {
  if (isLoading) {
    return (
      <div className="space-y-2">
        {Array(10).fill(0).map((_, i) => (
          <Skeleton key={i} className="h-20 w-full" />
        ))}
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {leads.map((lead) => {
        const isSelected = selectedLeadIds.includes(lead.id);
        
        return (
          <Link 
            key={lead.id}
            to={`/lead-detail?id=${lead.id}`}
            onClick={(e) => {
              // Don't navigate if clicking checkbox
              if (e.target.closest('[role="checkbox"]')) return;
              
              // Se não for Ctrl/Cmd+Click, previne navegação padrão e usa SPA
              if (!e.ctrlKey && !e.metaKey) {
                e.preventDefault();
                onViewLead(lead.id);
              }
            }}
            className="block"
          >
            <Card
              className={`border-slate-200 hover:shadow-md hover:scale-[1.01] transition-all duration-200 cursor-pointer ${
                isSelected ? "ring-2 ring-primary border-primary" : ""
              }`}
            >
            <CardContent className="p-4">
              <div className="flex items-center gap-4">
                {/* Checkbox */}
                {onToggleSelect && (
                  <div onClick={(e) => e.stopPropagation()}>
                    <Checkbox
                      checked={isSelected}
                      onCheckedChange={() => onToggleSelect(lead.id)}
                    />
                  </div>
                )}
              <div className={`w-12 h-12 bg-gradient-to-br ${getAvatarColor(lead.first_name)} rounded-full flex items-center justify-center flex-shrink-0`}>
                <span className="text-white font-bold">
                  {lead.first_name.charAt(0)}{lead.last_name.charAt(0)}
                </span>
              </div>
              
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  <h3 className="text-sm font-medium text-slate-900">
                    {lead.first_name} {lead.last_name}
                  </h3>
                  <Badge className={`${statusColors[lead.status]} border-0 font-medium pointer-events-none capitalize text-xs`}>
                    {lead.status}
                  </Badge>
                  {lead.market && (
                    <Badge 
                      variant="outline" 
                      className={`border-0 font-medium pointer-events-none text-xs ${
                        lead.market === 'US' 
                          ? 'bg-blue-100 text-blue-700' 
                          : lead.market === 'BR' 
                          ? 'bg-green-100 text-green-700'
                          : 'bg-gray-100 text-gray-700'
                      }`}
                    >
                      {lead.market}
                    </Badge>
                  )}
                </div>
                
                <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-slate-600">
                  {lead.phone && (
                    <div className="flex items-center gap-1.5">
                      <Phone className="w-3.5 h-3.5 text-slate-400" />
                      <span>{formatPhoneNumber(lead.phone)}</span>
                    </div>
                  )}
                  
                  {lead.email && (
                    <div className="flex items-center gap-1.5">
                      <Mail className="w-3.5 h-3.5 text-slate-400" />
                      <span className="truncate max-w-[200px]">{lead.email}</span>
                    </div>
                  )}

                  {lead.company && (
                    <div className="flex items-center gap-1.5">
                      <Building className="w-3.5 h-3.5 text-slate-400" />
                      <span className="truncate max-w-[150px]">{lead.company}</span>
                    </div>
                  )}
                </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </Link>
        );
      })}
    </div>
  );
}
